import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { MapPin, Mail, Clock, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '../hooks/useTranslation';

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    projectType: '',
    budget: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const projectTypes = [
    t('contact.projectTypes.residential'),
    t('contact.projectTypes.commercial'),
    t('contact.projectTypes.industrial'),
    t('contact.projectTypes.infrastructure'),
    t('contact.projectTypes.renovation'),
    t('contact.projectTypes.other')
  ];

  const budgetRanges = [
    t('contact.budgetRanges.under50k'),
    t('contact.budgetRanges.range50k250k'),
    t('contact.budgetRanges.range250k1m'),
    t('contact.budgetRanges.range1m5m'),
    t('contact.budgetRanges.over5m')
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      toast.success(t('contact.successMessage'), {
        description: t('contact.successDescription'),
        duration: 5000,
      });
      // Reset form
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        projectType: '',
        budget: '',
        message: ''
      });
    }, 1500);
  };

  const contactInfo = [
    {
      icon: MapPin,
      label: t('contact.address'),
      value: 'THE BLACK CHURCH, ST. MARY\'S PLACE\nDUBLIN 7, D07 P4AX\nIRELAND'
    },
    {
      icon: Mail,
      label: t('contact.email'),
      value: 'contact@probuildmarket.net'
    },
    {
      icon: Clock,
      label: t('contact.businessHours'),
      value: 'Mon - Fri: 9:00 - 18:00 CET\nSat: 10:00 - 14:00 CET'
    }
  ];

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">
            {t('contact.title')}
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="shadow-lg border-0">
              <CardHeader>
                <h3 className="text-2xl font-medium text-gray-900">
                  {t('contact.projectInquiryForm')}
                </h3>
                <p className="text-gray-600">
                  {t('contact.formDescription')}
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.fullName')} *
                      </label>
                      <Input
                        required
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder={t('common.placeholder.enterFullName')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.emailAddress')} *
                      </label>
                      <Input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder={t('common.placeholder.enterEmail')}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.companyName')}
                      </label>
                      <Input
                        value={formData.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        placeholder={t('common.placeholder.enterCompany')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.phoneNumber')}
                      </label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        placeholder={t('common.placeholder.enterPhone')}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.projectType')} *
                      </label>
                      <Select value={formData.projectType} onValueChange={(value) => handleInputChange('projectType', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder={t('contact.selectProjectType')} />
                        </SelectTrigger>
                        <SelectContent>
                          {projectTypes.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.budgetRange')}
                      </label>
                      <Select value={formData.budget} onValueChange={(value) => handleInputChange('budget', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder={t('contact.selectBudgetRange')} />
                        </SelectTrigger>
                        <SelectContent>
                          {budgetRanges.map((range) => (
                            <SelectItem key={range} value={range}>
                              {range}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.projectDetails')} *
                    </label>
                    <Textarea
                      required
                      rows={4}
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      placeholder={t('contact.describeProject')}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gray-900 hover:bg-gray-800 text-white py-3 text-lg"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        {t('contact.submitting')}
                      </>
                    ) : (
                      <>
                        <Send size={20} className="mr-2" />
                        {t('contact.sendProjectInquiry')}
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Information */}
          <div className="space-y-6">
            {/* Contact Details */}
            <Card className="shadow-lg border-0">
              <CardHeader>
                <h3 className="text-xl font-medium text-gray-900">
                  {t('contact.contactInformation')}
                </h3>
              </CardHeader>
              <CardContent className="space-y-6">
                {contactInfo.map((info, index) => {
                  const IconComponent = info.icon;
                  return (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent size={18} className="text-gray-600" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 mb-1">
                          {info.label}
                        </div>
                        <div className="text-gray-600 text-sm whitespace-pre-line">
                          {info.value}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Quick Response Promise */}
            <Card className="shadow-lg border-0 bg-gray-50">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle size={18} className="text-green-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 mb-2">
                      {t('contact.quickResponseGuarantee')}
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {t('contact.quickResponseText')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Services Badge */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">{t('contact.ourServices')}</h4>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">Import/Export</Badge>
                <Badge variant="outline">Logistics</Badge>
                <Badge variant="outline">B2B Platform</Badge>
                <Badge variant="outline">Consulting</Badge>
                <Badge variant="outline">Quality Control</Badge>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;